import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Typography, CircularProgress, Snackbar, Card, CardContent, Grid } from '@material-ui/core';
import { api_call, console_log, empty, getSmartcarUserId } from './helpers/untils';

// Standalone page to exercise the new /api/smartcar-v3/* routes end to end:
// Connect -> OAuth redirect -> SmartCarV3CallbackPage -> back here with tokens
// -> list vehicles. Not wired into real device creation - see SmartcarDevicePage.js
// for the v2 flow that creates devices.
const SmartcarV3TestPage = () => {
  const location = useLocation();

  const [accessToken, setAccessToken] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehicleInfoList, setVehicleInfoList] = useState(null);

  const [connections, setConnections] = useState(null);
  const [loadingConnections, setLoadingConnections] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const showToast = (msg) => {
    setToastMessage(msg)
    setSnackbarOpen(true)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const token = params.get("accessToken")
    if (token) {
      setAccessToken(token)
      loadVehicles(token)
    }
  }, []);

  const connect = async () => {
    setConnecting(true)
    const response = await api_call(`/api/smartcar-v3/get-login-url`);
    if (response.ok) {
      const res = await response.json()
      console_log("smartcar v3 login url:::", res)
      if (res.link) {
        window.location.href = res.link
        return
      }
    }
    setConnecting(false)
    showToast("Unable to get Smartcar login url")
  }

  const loadVehicles = async (token) => {
    setLoadingVehicles(true)
    const response = await api_call(`/api/smartcar-v3/get-all-vehicles?accessToken=${token}`);
    if (response.ok) {
      const res = await response.json()
      console_log("smartcar v3 vehicle list:::", res)
      setVehicleInfoList(res.vehicle_info_list || [])
    } else {
      showToast("Smartcar v3 API error")
      setVehicleInfoList([])
    }
    setLoadingVehicles(false)
  }

  const loadConnections = async () => {
    const sc_user_id = getSmartcarUserId()
    if (empty(sc_user_id)) {
      showToast("No smartcar_user_id found - connect with Smartcar first")
      return
    }
    setLoadingConnections(true)
    const response = await api_call(`/api/smartcar-v3/connections?smartcar_user_id=${sc_user_id}`);
    if (response.ok) {
      const res = await response.json()
      console_log("smartcar v3 connections:::", res)
      setConnections(res.data || res)
    } else {
      showToast("Smartcar v3 connections API error")
      setConnections(null)
    }
    setLoadingConnections(false)
  }

  const reset = () => {
    setAccessToken("")
    setVehicleInfoList(null)
    window.history.replaceState({}, '', '/smartcar-v3-test')
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '48px', gap: '24px' }}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        onClose={() => { setSnackbarOpen(false) }}
        autoHideDuration={3000}
        message={toastMessage}
      />

      <Typography variant="h5">Smartcar API v3 Test</Typography>

      {empty(accessToken) ? (
        <Button variant="contained" color="secondary" disabled={connecting} onClick={connect}>
          {connecting ? "Redirecting..." : "Connect with Smartcar"}
        </Button>
      ) : (
        <>
          <Typography variant="body2" style={{ wordBreak: 'break-all', maxWidth: '480px', textAlign: 'center' }}>
            accessToken: {accessToken}
          </Typography>
          <Button variant="outlined" onClick={reset}>Start over</Button>

          <div style={{ width: '480px', maxWidth: '100%' }}>
            {loadingVehicles ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></div>
            ) : (
              <Grid container direction="column" spacing={2}>
                {(!empty(vehicleInfoList) && vehicleInfoList.length > 0) ? (
                  vehicleInfoList.map((vehicle, index) => (
                    <Grid item key={vehicle.id || index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            id: {vehicle.id}
                          </Typography>
                          {vehicle.vin && (
                            <Typography variant="body2" color="textSecondary">
                              vin: {vehicle.vin}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item>
                    <Typography variant="body2" align="center">No vehicles found</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </div>
        </>
      )}

      <div style={{ width: '480px', maxWidth: '100%', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #ddd' }}>
        <Typography variant="h6" gutterBottom>Connections</Typography>
        <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
          smartcar_user_id: {getSmartcarUserId() || '(none)'}
        </Typography>
        <Button
          variant="outlined"
          disabled={loadingConnections || empty(getSmartcarUserId())}
          onClick={loadConnections}
          style={{ marginTop: '8px', marginBottom: '16px' }}
        >
          {loadingConnections ? "Loading..." : "Get Connections"}
        </Button>

        {loadingConnections ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></div>
        ) : (
          !empty(connections) && (
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
              {JSON.stringify(connections, null, 2)}
            </pre>
          )
        )}
      </div>
    </div>
  );
};

export default SmartcarV3TestPage;
